import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  FeatureFlagContextType,
  FeatureFlagProviderProps,
  FeatureFlag,
} from "@/types/feature-flags";
import { FeatureFlagKey } from "@/types/feature-flags";
import {
  featureFlagManager,
  FeatureFlagManager,
} from "@/lib/feature-flags/manager";

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(
  undefined
);

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
  children,
  service: customService,
  fallbackFlags = {},
  overrideFlags = {},
  accountId,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Initialize the manager with custom service if provided
  useEffect(() => {
    if (customService) {
      // If a custom service is provided, we need to create a new manager instance
      // This is mainly for testing or special cases
      const manager = FeatureFlagManager.getInstance(customService);
      manager.initialize(accountId);
    } else {
      // Use the default singleton manager
      featureFlagManager.initialize(accountId);
    }
  }, [customService, accountId]);

  // Apply override flags if provided
  useEffect(() => {
    if (Object.keys(overrideFlags).length > 0) {
      featureFlagManager.updateOverrideFlags(overrideFlags);
    }
  }, [overrideFlags]);

  // Subscribe to manager changes
  useEffect(() => {
    const unsubscribe = featureFlagManager.subscribe(() => {
      setRefreshTrigger((prev) => prev + 1);
    });

    // Initial load
    const initializeFlags = async () => {
      try {
        setIsLoading(true);
        setError(null);
        await featureFlagManager.initialize();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load feature flags";
        setError(errorMessage);
        console.error("Feature flag loading error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeFlags();

    return unsubscribe;
  }, []);

  // Direct access to manager methods (no state dependencies)
  const isEnabled = useCallback((flagKey: FeatureFlagKey): boolean => {
    return featureFlagManager.isEnabled(flagKey);
  }, []);

  const getFlag = useCallback(
    (flagKey: FeatureFlagKey): FeatureFlag | undefined => {
      return featureFlagManager.getFlag(flagKey);
    },
    []
  );

  const getAllFlags = useCallback((): Partial<
    Record<FeatureFlagKey, FeatureFlag>
  > => {
    return featureFlagManager.getAllFlags();
  }, []);

  const refreshFlags = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      await featureFlagManager.refreshFlags();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to refresh feature flags";
      setError(errorMessage);
      console.error("Feature flag refresh error:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateOverrideFlags = useCallback(
    (overrideFlags: Partial<Record<FeatureFlagKey, FeatureFlag>>) => {
      featureFlagManager.updateOverrideFlags(overrideFlags);
    },
    []
  );

  const contextValue: FeatureFlagContextType = {
    isEnabled,
    getFlag,
    getAllFlags,
    refreshFlags,
    updateOverrideFlags,
    isLoading,
    error,
  };

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider"
    );
  }
  return context;
};

// Convenience hook for checking if a specific flag is enabled
// This version subscribes to changes and will cause re-renders
export const useFeatureFlagWithSubscription = (
  flagKey: FeatureFlagKey
): boolean => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flagKey);
};

// Hook for getting a specific flag with all its properties
// This version subscribes to changes and will cause re-renders
export const useFeatureFlagDetailsWithSubscription = (
  flagKey: FeatureFlagKey
): FeatureFlag | undefined => {
  const { getFlag } = useFeatureFlags();
  return getFlag(flagKey);
};

// Hook for updating override flags
export const useUpdateOverrideFlags = () => {
  const { updateOverrideFlags } = useFeatureFlags();
  return updateOverrideFlags;
};
